import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, Lightbulb, Sparkles } from 'lucide-react';
import {
  DEVELOPMENT_COPY,
  DEVELOPMENT_STAGES,
  REPHRASE_GUIDE,
  RECORD_STATUS_LABELS,
  ROLES,
  WRITING_STAGE_HINTS,
  writingGuideFor,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { StageIcon } from '@/components/development';
import { Button, Card, ErrorNote, SectionTitle, Spinner, Textarea } from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { cn, formatDate } from '@/lib/format';
import { useRole } from '@/store/auth';
import type {
  AiDraftResult,
  AiWriterStatus,
  DevelopmentItemRef,
  DevelopmentRecordDetail,
  LanguageWarningRef,
  ObservationSummary,
  PeriodRef,
} from '@/types';

interface CurrentPeriod {
  age: { totalMonths: number; label: string; corrected: boolean };
  period: PeriodRef | null;
}

interface SaveResult {
  ok: boolean;
  warnings: LanguageWarningRef[];
}

/**
 * 발달기록 작성 — 명세서 §6.7
 *
 * 흐름: 구간 확인 → 영역별 항목에 L1–L7 체크(관찰된 것만) + 서술 코멘트
 *       → 종합 한 줄 요약 → (설정 시) 원장 검토 → 학부모 공개
 *
 * §6.9 언어 정책은 **차단이 아니라 제안**이다. 저장은 되고 경고만 함께 보여 준다.
 */
export function DevelopmentEditorPage() {
  const { childId = '' } = useParams();
  const queryClient = useQueryClient();
  const role = useRole();
  const isDirector = role === ROLES.DIRECTOR;

  const [levels, setLevels] = useState<Record<string, number>>({});
  const [itemComments, setItemComments] = useState<Record<string, string>>({});
  const [domainComments, setDomainComments] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState('');
  const [warnings, setWarnings] = useState<LanguageWarningRef[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** 항목별 근거 관찰기록 링크 (§6.1-4) — itemId → 관찰기록 id 목록 */
  const [evidence, setEvidence] = useState<Record<string, string[]>>({});
  const [evidenceOpen, setEvidenceOpen] = useState<Record<string, boolean>>({});

  /** AI 초안 흔적 (§5.23) — 필드 키('summary' 또는 영역 코드) → 마지막 초안 원문 */
  const [aiDrafts, setAiDrafts] = useState<Record<string, string>>({});
  const [aiAssisted, setAiAssisted] = useState<Record<string, boolean>>({});
  const [aiWarnings, setAiWarnings] = useState<LanguageWarningRef[]>([]);
  const [guideOpen, setGuideOpen] = useState<Record<string, boolean>>({});

  const current = useQuery({
    queryKey: ['development', 'current-period', childId],
    queryFn: () => apiData<CurrentPeriod>(`/development/current-period?childId=${childId}`),
    enabled: Boolean(childId),
  });

  const periodId = current.data?.period?.id ?? '';

  const items = useQuery({
    queryKey: ['development', 'items', periodId],
    queryFn: () => apiData<DevelopmentItemRef[]>(`/development/items?periodId=${periodId}`),
    enabled: Boolean(periodId),
  });

  const record = useQuery({
    queryKey: ['development', 'record', childId, periodId],
    queryFn: async () => {
      const created = await apiData<{ id: string }>('/development/records', {
        method: 'POST',
        body: { childId, periodId },
      });
      return apiData<DevelopmentRecordDetail>(`/development/records/${created.id}`);
    },
    enabled: Boolean(childId && periodId),
  });

  /** AI 도우미 사용 가능 여부·오늘 사용량 — 키가 없으면 버튼을 감춘다 (§5.23) */
  const aiStatus = useQuery({
    queryKey: ['ai', 'writer', 'status'],
    queryFn: () => apiData<AiWriterStatus>('/ai/writer/status'),
  });

  /** 이 아이의 관찰기록 — 근거 연결 후보 (§5.14) */
  const observations = useQuery({
    queryKey: ['observations', { childId }],
    queryFn: () => apiData<ObservationSummary[]>(`/observations?childId=${childId}`),
    enabled: Boolean(childId),
  });

  // 서버에 저장된 값을 폼 상태로 옮긴다.
  useEffect(() => {
    const data = record.data;
    if (!data) return;
    setSummary(data.summary ?? '');
    setLevels(Object.fromEntries(data.checks.map((check) => [check.itemId, check.level])));
    setItemComments(
      Object.fromEntries(data.checks.filter((c) => c.comment).map((c) => [c.itemId, c.comment!])),
    );
    setDomainComments(
      Object.fromEntries(
        data.domainComments.map((comment) => {
          const domain = data.domains.find((d) => d.id === comment.domainId);
          return [domain?.code ?? comment.domainId, comment.comment];
        }),
      ),
    );
    setEvidence(
      Object.fromEntries(
        data.checks
          .filter((check) => (check.evidenceRefs ?? []).length > 0)
          .map((check) => [
            check.itemId,
            (check.evidenceRefs ?? [])
              .filter((ref) => ref.type === 'observation')
              .map((ref) => ref.id),
          ]),
      ),
    );
  }, [record.data]);

  const byDomain = useMemo(() => {
    const map = new Map<string, { name: string; items: DevelopmentItemRef[] }>();
    for (const item of items.data ?? []) {
      const entry = map.get(item.domain.code) ?? { name: item.domain.name, items: [] };
      entry.items.push(item);
      map.set(item.domain.code, entry);
    }
    return map;
  }, [items.data]);

  const recordId = record.data?.id;

  const save = useMutation({
    mutationFn: () =>
      apiData<SaveResult>(`/development/records/${recordId}`, {
        method: 'PATCH',
        body: {
          summary: summary.trim() || undefined,
          checks: Object.entries(levels).map(([itemId, level]) => ({
            itemId,
            level,
            comment: itemComments[itemId]?.trim() || undefined,
            evidenceRefs: (evidence[itemId] ?? []).map((id) => ({ type: 'observation', id })),
          })),
          domainComments: Object.entries(domainComments)
            .filter(([, comment]) => comment.trim())
            .map(([domainCode, comment]) => ({
              domainCode,
              comment: comment.trim(),
              // §5.23 교사 최종 책임 — 초안을 썼다는 사실을 함께 기록한다 (수정 여부와 무관)
              aiAssisted: Boolean(aiAssisted[domainCode]),
            })),
        },
      }),
    onSuccess: (result) => {
      setWarnings(result.warnings);
      setError(null);
      setMessage(
        result.warnings.length > 0
          ? '저장했어요. 아래 표현을 한 번 더 살펴봐 주세요.'
          : '저장했어요.',
      );
      void queryClient.invalidateQueries({ queryKey: ['development'] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : '저장하지 못했어요.'),
  });

  /** AI 초안·다듬기 (§5.23) — 응답은 항상 편집 칸에 담겨 교사 검토를 거친다 */
  const aiDraft = useMutation({
    mutationFn: (input: { target: 'DOMAIN' | 'SUMMARY'; domainCode?: string; mode: 'DRAFT' | 'POLISH' }) =>
      apiData<AiDraftResult>('/ai/writer/dev-comment', {
        method: 'POST',
        body: {
          recordId,
          target: input.target,
          domainCode: input.domainCode,
          mode: input.mode,
          text:
            input.mode === 'POLISH'
              ? input.target === 'SUMMARY'
                ? summary
                : (domainComments[input.domainCode ?? ''] ?? '')
              : undefined,
        },
      }).then((result) => ({ result, input })),
    onSuccess: ({ result, input }) => {
      const key = input.target === 'SUMMARY' ? 'summary' : (input.domainCode ?? '');
      if (input.target === 'SUMMARY') setSummary(result.draft);
      else setDomainComments((prev) => ({ ...prev, [key]: result.draft }));
      setAiDrafts((prev) => ({ ...prev, [key]: result.draft }));
      setAiAssisted((prev) => ({ ...prev, [key]: true }));
      setAiWarnings(result.warnings);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['ai', 'writer', 'status'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'AI 초안을 만들지 못했어요.'),
  });

  /** 지금 AI가 채우는 중인 필드 키 — 버튼별 로딩 표시용 */
  const aiBusyKey = aiDraft.isPending
    ? aiDraft.variables?.target === 'SUMMARY'
      ? 'summary'
      : aiDraft.variables?.domainCode
    : undefined;

  /** AI 초안을 한 글자도 고치지 않고 저장하려 하면 한 번 확인한다 (§5.23 교사 최종 책임) */
  function handleSave() {
    const untouched = Object.entries(aiDrafts).some(([key, draft]) => {
      const currentText = key === 'summary' ? summary : (domainComments[key] ?? '');
      return currentText.trim() !== '' && currentText.trim() === draft.trim();
    });
    if (
      untouched &&
      !window.confirm('AI 초안을 수정 없이 저장하려고 해요. 관찰한 사실과 맞는지 확인하셨나요?')
    ) {
      return;
    }
    save.mutate();
  }

  const act = useMutation({
    mutationFn: (action: 'submit' | 'approve' | 'publish') =>
      apiData(`/development/records/${recordId}/${action}`, { method: 'POST' }),
    onSuccess: (_data, action) => {
      setError(null);
      setMessage(
        action === 'publish'
          ? '가정에 공개했어요. 보호자에게 알림이 갔어요.'
          : action === 'approve'
            ? '검토를 마쳤어요.'
            : '원장님께 검토를 요청했어요.',
      );
      void queryClient.invalidateQueries({ queryKey: ['development'] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : '처리하지 못했어요.'),
  });

  if (current.isPending || record.isPending) return <Spinner label="기록을 준비하는 중이에요" />;

  if (current.isError || record.isError || !record.data) {
    return (
      <Screen withNav={false}>
        <AppBar title="발달기록" back />
        <ErrorNote
          message="기록을 불러오지 못했어요."
          onRetry={() => {
            void current.refetch();
            void record.refetch();
          }}
        />
      </Screen>
    );
  }

  const data = record.data;
  const checkedCount = Object.keys(levels).length;

  return (
    <Screen withNav={false}>
      <AppBar
        title={`${data.child.name} 성장 기록`}
        subtitle={`${data.period.label} · ${RECORD_STATUS_LABELS[data.status]}`}
        back
      />

      <div className="flex flex-col gap-4 p-4">
        <p className="rounded-sm bg-primary-soft px-3 py-2.5 text-sm text-primary-strong">
          <b>{DEVELOPMENT_COPY.everyChildOwnPace}</b>
          <br />
          관찰한 항목만 체크해 주세요. 모든 항목을 채우지 않아도 괜찮아요.
        </p>

        {[...byDomain.entries()].map(([domainCode, domain]) => (
          <section key={domainCode}>
            <SectionTitle>{domain.name}</SectionTitle>
            <Card className="flex flex-col gap-4">
              {domain.items.map((item) => (
                <div key={item.id}>
                  <p className="mb-2 text-base font-medium">{item.text}</p>
                  <StagePicker
                    value={levels[item.id]}
                    onChange={(level) =>
                      setLevels((prev) => {
                        if (prev[item.id] === level) {
                          const next = { ...prev };
                          delete next[item.id];
                          return next;
                        }
                        return { ...prev, [item.id]: level };
                      })
                    }
                  />
                  {levels[item.id] && (
                    <Textarea
                      rows={2}
                      className="mt-2"
                      placeholder="어떤 모습이었는지 한 줄 남겨 주세요 (선택)"
                      value={itemComments[item.id] ?? ''}
                      onChange={(e) =>
                        setItemComments((prev) => ({ ...prev, [item.id]: e.target.value }))
                      }
                    />
                  )}

                  {/* §6.1-4 근거 있는 기록 — 관찰기록을 이 체크의 근거로 잇는다 */}
                  {levels[item.id] && (observations.data?.length ?? 0) > 0 && (
                    <div className="mt-1.5">
                      <button
                        type="button"
                        className="text-sm text-primary-strong underline-offset-2 hover:underline"
                        onClick={() =>
                          setEvidenceOpen((prev) => ({ ...prev, [item.id]: !prev[item.id] }))
                        }
                      >
                        {(evidence[item.id]?.length ?? 0) > 0
                          ? `근거 관찰기록 ${evidence[item.id].length}건 연결됨`
                          : '근거 관찰기록 연결하기'}
                      </button>

                      {evidenceOpen[item.id] && (
                        <ul className="mt-1.5 flex flex-col gap-1.5">
                          {observations.data?.map((observation) => {
                            const selected = evidence[item.id]?.includes(observation.id) ?? false;
                            return (
                              <li key={observation.id}>
                                <button
                                  type="button"
                                  aria-pressed={selected}
                                  onClick={() =>
                                    setEvidence((prev) => {
                                      const list = prev[item.id] ?? [];
                                      return {
                                        ...prev,
                                        [item.id]: selected
                                          ? list.filter((id) => id !== observation.id)
                                          : [...list, observation.id],
                                      };
                                    })
                                  }
                                  className={cn(
                                    'w-full rounded-sm border px-2.5 py-2 text-left text-sm',
                                    selected
                                      ? 'border-primary bg-primary-soft'
                                      : 'border-line bg-surface',
                                  )}
                                >
                                  <span className="mr-1.5 text-ink-sub">
                                    {formatDate(observation.observedOn)}
                                  </span>
                                  <span className="line-clamp-2">{observation.content}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              ))}

              <div>
                <Textarea
                  rows={3}
                  placeholder={`${domain.name} 영역에 대한 이야기 (선택)`}
                  value={domainComments[domainCode] ?? ''}
                  onChange={(e) =>
                    setDomainComments((prev) => ({ ...prev, [domainCode]: e.target.value }))
                  }
                />

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {aiStatus.data?.available && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        icon={<Sparkles size={14} />}
                        loading={aiBusyKey === domainCode && aiDraft.variables?.mode === 'DRAFT'}
                        onClick={() =>
                          aiDraft.mutate({ target: 'DOMAIN', domainCode, mode: 'DRAFT' })
                        }
                      >
                        AI 초안
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!domainComments[domainCode]?.trim()}
                        loading={aiBusyKey === domainCode && aiDraft.variables?.mode === 'POLISH'}
                        onClick={() =>
                          aiDraft.mutate({ target: 'DOMAIN', domainCode, mode: 'POLISH' })
                        }
                      >
                        다듬기
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setGuideOpen((prev) => ({ ...prev, [domainCode]: !prev[domainCode] }))
                    }
                  >
                    {guideOpen[domainCode] ? '가이드 닫기' : '문장 가이드'}
                  </Button>
                </div>

                {aiAssisted[domainCode] && (
                  <p className="mt-1.5 text-sm text-ink-sub">
                    AI 초안이 담겼어요. 입력한 관찰만 근거로 하니, 사실과 다른 부분은 꼭 고쳐
                    주세요.
                  </p>
                )}

                {guideOpen[domainCode] && <WritingGuideBox domainCode={domainCode} />}
              </div>
            </Card>
          </section>
        ))}

        <section>
          <SectionTitle>종합 한 줄 요약</SectionTitle>
          <Card>
            <p className="mb-2 text-sm text-ink-sub">가정 화면 맨 위에 보이는 문장이에요.</p>
            <Textarea
              rows={3}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="예) 하늘이는 요즘 손으로 만지고 두드리며 세상을 탐색하고 있어요."
            />

            {aiStatus.data?.available && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  icon={<Sparkles size={14} />}
                  loading={aiBusyKey === 'summary' && aiDraft.variables?.mode === 'DRAFT'}
                  onClick={() => aiDraft.mutate({ target: 'SUMMARY', mode: 'DRAFT' })}
                >
                  AI 초안
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!summary.trim()}
                  loading={aiBusyKey === 'summary' && aiDraft.variables?.mode === 'POLISH'}
                  onClick={() => aiDraft.mutate({ target: 'SUMMARY', mode: 'POLISH' })}
                >
                  다듬기
                </Button>
                <span className="ml-auto text-sm text-ink-sub tnum">
                  오늘 {aiStatus.data.usedToday}/{aiStatus.data.dailyLimit}회
                </span>
              </div>
            )}

            {aiAssisted.summary && (
              <p className="mt-1.5 text-sm text-ink-sub">
                AI 초안이 담겼어요. 입력한 관찰만 근거로 하니, 사실과 다른 부분은 꼭 고쳐 주세요.
              </p>
            )}
          </Card>
        </section>

        {aiWarnings.length > 0 && (
          <Card className="border border-secondary">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-ink">
              <CircleAlert size={15} /> AI 초안에 살펴볼 표현이 있어요
            </p>
            <ul className="flex flex-col gap-2 text-sm">
              {aiWarnings.map((warning, index) => (
                <li key={`${warning.term}-${index}`}>
                  <b>“{warning.term}”</b> — {warning.reason}
                  <br />
                  <span className="text-ink-sub">제안: {warning.suggestions.join(' / ')}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* §6.9 대체 표현 가이드 — 작성 화면에 상시 제공 */}
        <Card className="bg-info-soft">
          <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-ink">
            <Lightbulb size={15} /> 이렇게 바꿔 보세요
          </p>
          <ul className="flex flex-col gap-1 text-sm text-ink">
            {REPHRASE_GUIDE.map((guide) => (
              <li key={guide.before}>
                <span className="text-ink-sub line-through">{guide.before}</span>
                {' → '}
                <b>{guide.after}</b>
              </li>
            ))}
          </ul>
        </Card>

        {warnings.length > 0 && (
          <Card className="border border-secondary">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-ink">
              <CircleAlert size={15} /> 살펴볼 표현이 있어요
            </p>
            <ul className="flex flex-col gap-2 text-sm">
              {warnings.map((warning, index) => (
                <li key={`${warning.term}-${index}`}>
                  <b>“{warning.term}”</b> — {warning.reason}
                  <br />
                  <span className="text-ink-sub">제안: {warning.suggestions.join(' / ')}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {message && (
          <p className="rounded-sm bg-primary-soft px-3 py-2 text-sm text-primary-strong">{message}</p>
        )}
        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        <div className="flex flex-col gap-2 pb-6">
          <p className="text-center text-sm text-ink-sub">
            관찰한 항목 <b className="tnum">{checkedCount}</b>개
          </p>

          <Button block loading={save.isPending} onClick={handleSave}>
            저장하기
          </Button>

          {data.status === 'DRAFT' && (
            <Button
              variant="outline"
              block
              loading={act.isPending}
              onClick={() => act.mutate('submit')}
            >
              원장님께 검토 요청
            </Button>
          )}

          {isDirector && data.status === 'REVIEW' && !data.approvedAt && (
            <Button
              variant="outline"
              block
              loading={act.isPending}
              onClick={() => act.mutate('approve')}
            >
              검토 완료 처리
            </Button>
          )}

          <Button
            variant="secondary"
            block
            loading={act.isPending}
            disabled={checkedCount === 0 || !summary.trim()}
            onClick={() => act.mutate('publish')}
          >
            {data.status === 'PUBLISHED' ? '수정 내용 다시 공개하기' : '가정에 공개하기'}
          </Button>
        </div>
      </div>
    </Screen>
  );
}

/** 문장 가이드 (§5.23 모드③) — AI 없이도 쓰는 정적 프레임·예시 */
function WritingGuideBox({ domainCode }: { domainCode: string }) {
  const guide = writingGuideFor(domainCode);
  if (!guide) return null;

  return (
    <div className="mt-2 rounded-sm bg-bg px-3 py-2.5 text-sm">
      <p className="mb-1.5 font-semibold">문장 프레임</p>
      <ul className="mb-3 flex flex-col gap-1 text-ink-sub">
        {guide.frames.map((frame) => (
          <li key={frame}>· {frame}</li>
        ))}
      </ul>
      <p className="mb-1.5 font-semibold">좋은 예</p>
      <ul className="mb-3 flex flex-col gap-1 text-ink-sub">
        {guide.examples.map((example) => (
          <li key={example}>· {example}</li>
        ))}
      </ul>
      <p className="mb-1.5 font-semibold">단계별 힌트</p>
      <ul className="flex flex-col gap-1 text-ink-sub">
        {WRITING_STAGE_HINTS.map((hint) => (
          <li key={hint.levels}>
            <b className="font-medium text-ink">{hint.levels}</b> — {hint.hint}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** L1–L7 단계 선택 — 그린 명도 스케일만 쓴다 (§7.4) */
function StagePicker({
  value,
  onChange,
}: {
  value?: number;
  onChange: (level: number) => void;
}) {
  return (
    <div className="scroll-x -mx-1">
      <div className="flex w-max gap-1.5 px-1">
        {DEVELOPMENT_STAGES.map((stage) => {
          const active = value === stage.level;
          return (
            <button
              key={stage.code}
              type="button"
              onClick={() => onChange(stage.level)}
              aria-pressed={active}
              className={cn(
                'flex w-[58px] flex-col items-center gap-1 rounded-sm px-1 py-2 transition-all active:scale-95',
                active && 'ring-2 ring-primary-strong',
              )}
              style={{ backgroundColor: `${stage.color}${active ? 'FF' : '66'}` }}
            >
              <StageIcon level={stage.level} size={22} />
              <span
                className="text-[11px] font-semibold"
                style={{ color: stage.level >= 5 && active ? '#FFFFFF' : '#2E5F4C' }}
              >
                {stage.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
